import React, {
  useRef, useEffect, useState, useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { commonActions } from '@/redux/modules';
import * as Styles from './Notifications.styles';
import { Toast } from '../Toast';
import { Alert } from 'gatorcomponents';


const Notifications = () => {
  const notifications = useSelector(state => state.common.notifications);
  const dispatch = useDispatch();
  const domNode = document.body;
  const wrapperRef = useRef();
  const [maxWidth, setMaxWidth] = useState('513px');
  const [closingIds, setClosingIds] = useState(new Set());
  const notificationsRef = useRef(notifications);

  // Map to store stable IDs for each notification
  const notificationIdsMapRef = useRef(new Map()); // Map: notification object -> uniqueId
  const notificationTimeoutsRef = useRef(new Map()); // Map: uniqueId -> timeoutId
  const nextIdRef = useRef(0);

  // Keep notifications ref in sync
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Generate or retrieve stable ID for a notification
  const getNotificationId = useCallback((notification) => {
    if (!notificationIdsMapRef.current.has(notification)) {
      const newId = nextIdRef.current;
      nextIdRef.current += 1;
      notificationIdsMapRef.current.set(notification, newId);
    }
    return notificationIdsMapRef.current.get(notification);
  }, []);

  // Clean up IDs for notifications that no longer exist
  useEffect(() => {
    const currentNotificationSet = new Set(notifications);
    const idsToRemove = [];

    notificationIdsMapRef.current.forEach((id, notification) => {
      if (!currentNotificationSet.has(notification)) {
        idsToRemove.push({ notification, id });
      }
    });

    idsToRemove.forEach(({ notification, id }) => {
      notificationIdsMapRef.current.delete(notification);
      // Clean up timeout if exists
      const timeoutId = notificationTimeoutsRef.current.get(id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        notificationTimeoutsRef.current.delete(id);
      }
    });
  }, [notifications]);

  const updatePositions = useCallback(() => {
    if (wrapperRef.current && wrapperRef.current.childNodes.length) {
      let lenght = 0;
      wrapperRef.current.childNodes.forEach((notification, index) => {
        const notficiationItem = notification;
        notficiationItem.style.bottom = `${lenght}px`;
        lenght += wrapperRef.current.childNodes[index].offsetHeight + 10;
      });
    }
  }, []);

  const removeNotificationByObject = useCallback((notification) => {
    const notificationId = getNotificationId(notification);

    // Clear timeout if exists
    const timeoutId = notificationTimeoutsRef.current.get(notificationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      notificationTimeoutsRef.current.delete(notificationId);
    }

    // Start closing animation
    setClosingIds(prev => new Set(prev).add(notificationId));

    // Wait for animation to complete before removing
    setTimeout(() => {
      // Find current index using the notification object from ref
      const currentIndex = notificationsRef.current.indexOf(notification);
      if (currentIndex !== -1) {
        dispatch(commonActions.notifications.remove(currentIndex));
      }
      setClosingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }, 300); // Match animation duration
  }, [dispatch, getNotificationId]);

  useEffect(() => {
    if (notifications.length) {
      setTimeout(() => updatePositions(), 100);
    }
  }, [notifications.length, updatePositions]);

  // Auto-close notifications with autoClose prop
  useEffect(() => {
    notifications.forEach((notification) => {
      // Default autoClose to 5000ms (5 seconds) if not specified
      // Set to false or 0 to disable auto-close
      const autoClose = notification.toast?.autoClose !== undefined
        ? notification.toast.autoClose
        : 5000;

      // Skip if autoClose is disabled (false or 0)
      if (autoClose === false || autoClose === 0) return;

      const notificationId = getNotificationId(notification);

      // Skip if already has a timeout set for this notification (most important check)
      if (notificationTimeoutsRef.current.has(notificationId)) {
        return;
      }

      // Skip if notification is already closing
      if (closingIds.has(notificationId)) {
        return;
      }

      // Set timeout for this notification
      const timeoutId = setTimeout(() => {
        // Remove timeout from ref immediately
        notificationTimeoutsRef.current.delete(notificationId);

        // Start closing animation
        setClosingIds(prev => new Set(prev).add(notificationId));

        // Wait for animation to complete before removing
        setTimeout(() => {
          const currentIndex = notificationsRef.current.indexOf(notification);
          if (currentIndex !== -1) {
            dispatch(commonActions.notifications.remove(currentIndex));
          }
          setClosingIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(notificationId);
            return newSet;
          });
        }, 300);
      }, autoClose);

      notificationTimeoutsRef.current.set(notificationId, timeoutId);
    });
  }, [notifications, getNotificationId, closingIds, dispatch]);

  return ReactDOM.createPortal(
    <Styles.Wrapper
      ref={wrapperRef}
      maxWidth={maxWidth}
      hasToast={notifications.length && notifications[0].toast}
    >
      {notifications.map((notification, index) => {
        if (notification.maxWidth !== maxWidth) {
          setMaxWidth(notification.maxWidth);
        }

        const notificationId = getNotificationId(notification);
        const isClosing = closingIds.has(notificationId);

        if (notification.toast) {
          return (
            <Toast
              key={`toast-${notificationId}`}
              onClose={() => removeNotificationByObject(notification)}
              index={index}
              forceClosing={isClosing}
              {...notification.toast}
            />
          );
        }

        return (
          <Styles.Notification
            index={index}
            data-testid={`notification-item-${index}`}
            maxWidth={notification.maxWidth}
            background={notification.backGround}
            key={`notification-${notificationId}`}
          >
            <Alert
              width="full"
              variant={notification.type}
              description={notification.label}
              onClose={() => removeNotificationByObject(notification)}
            />
          </Styles.Notification>
        );
      })}
    </Styles.Wrapper>,
    domNode,
  );
};

export default Notifications;
